import { c as _c } from "react/compiler-runtime";
import { LazyImage, type LazyImagePropertiesType } from "@website/ui/lazy-image";
import { Skeleton } from "@website/ui/skeleton";
import { FC, ReactEventHandler, useEffect, useRef, useState } from "react";
const IMAGE_LOAD_ROOT_MARGIN = "300px 0px";
type AccommodationCardImagePropertiesType = LazyImagePropertiesType & {
  deferUntilVisible?: boolean;
};
const useRenderWhenVisible = deferUntilVisible => {
  const $ = _c(6);
  const containerReference = useRef(null);
  const [shouldRender, setShouldRender] = useState(!deferUntilVisible);
  let t0;
  let t1;
  if ($[0] !== deferUntilVisible || $[1] !== shouldRender) {
    t0 = () => {
      if (shouldRender) {
        return;
      }
      if (!deferUntilVisible || typeof IntersectionObserver === "undefined") {
        setShouldRender(true);
        return;
      }
      const container = containerReference.current;
      if (!container) {
        return;
      }
      const observer = new IntersectionObserver(t2 => {
        const [entry] = t2;
        if (!entry.isIntersecting) {
          return;
        }
        setShouldRender(true);
        observer.disconnect();
      }, {
        rootMargin: IMAGE_LOAD_ROOT_MARGIN
      });
      observer.observe(container);
      return () => observer.disconnect();
    };
    t1 = [deferUntilVisible, shouldRender];
    $[0] = deferUntilVisible;
    $[1] = shouldRender;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== shouldRender) {
    t2 = {
      containerReference,
      shouldRender
    };
    $[4] = shouldRender;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const AccommodationCardImage: FC<AccommodationCardImagePropertiesType> = t0 => {
  const $ = _c(30);
  let fallback;
  let onLoad;
  let properties;
  let src;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className: t1,
      deferUntilVisible: t2,
      fallback,
      onLoad,
      src,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = fallback;
    $[2] = onLoad;
    $[3] = properties;
    $[4] = src;
    $[5] = t1;
    $[6] = t2;
  } else {
    fallback = $[1];
    onLoad = $[2];
    properties = $[3];
    src = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const className = t1 === undefined ? "" : t1;
  const deferUntilVisible = t2 === undefined ? false : t2;
  const imageSourceKey = String(src || fallback || "");
  const {
    containerReference,
    shouldRender
  } = useRenderWhenVisible(deferUntilVisible);
  const [loadedSourceKey, setLoadedSourceKey] = useState();
  const isLoaded = loadedSourceKey === imageSourceKey;
  let t3;
  let t4;
  if ($[7] !== containerReference || $[8] !== imageSourceKey || $[9] !== shouldRender) {
    t3 = () => {
      if (!shouldRender) {
        return;
      }
      const image = containerReference.current?.querySelector("img");
      if (image?.complete) {
        setLoadedSourceKey(imageSourceKey);
      }
    };
    t4 = [containerReference, imageSourceKey, shouldRender];
    $[7] = containerReference;
    $[8] = imageSourceKey;
    $[9] = shouldRender;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== imageSourceKey || $[13] !== onLoad) {
    t5 = event => {
      setLoadedSourceKey(imageSourceKey);
      onLoad?.(event);
    };
    $[12] = imageSourceKey;
    $[13] = onLoad;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleLoad = t5;
  const t6 = `pointer-events-none absolute inset-0 transition-opacity ${isLoaded ? "opacity-0" : "opacity-100"}`;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Skeleton className="!rounded-[14px]" />;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6) {
    t8 = <div aria-hidden="true" className={t6}>{t7}</div>;
    $[16] = t6;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== className || $[19] !== fallback || $[20] !== handleLoad || $[21] !== isLoaded || $[22] !== properties || $[23] !== shouldRender || $[24] !== src) {
    t9 = shouldRender && <LazyImage {...properties} className={`${className} ${isLoaded ? "opacity-100" : "opacity-0"}`} fallback={fallback} onLoad={handleLoad} src={src} />;
    $[18] = className;
    $[19] = fallback;
    $[20] = handleLoad;
    $[21] = isLoaded;
    $[22] = properties;
    $[23] = shouldRender;
    $[24] = src;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== containerReference || $[27] !== t8 || $[28] !== t9) {
    t10 = <div className="relative size-full" ref={containerReference}>{t8}{t9}</div>;
    $[26] = containerReference;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
};